import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Keyboard } from 'lucide-react';

interface ShortcutInfo {
  keys: string[];
  description: string;
}

const SHORTCUTS: ShortcutInfo[] = [
  { keys: ['Ctrl', 'K'], description: 'Focus search input' },
  { keys: ['Ctrl', 'Enter'], description: 'Copy focused style' },
  { keys: ['Ctrl', 'Shift', 'R'], description: 'Random style (Surprise Me)' },
  { keys: ['Ctrl', 'Shift', 'F'], description: 'Toggle favorites filter' },
  { keys: ['Escape'], description: 'Clear search & filters' },
  { keys: ['?'], description: 'Show keyboard shortcuts' },
];

interface KeyboardShortcutsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function KeyboardShortcuts({ open, onOpenChange }: KeyboardShortcutsProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard size={20} />
            Keyboard Shortcuts
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {SHORTCUTS.map((shortcut, index) => (
            <div
              key={index}
              className="flex items-center justify-between py-2 border-b border-border/50 last:border-0"
            >
              <span className="text-sm text-muted-foreground">
                {shortcut.description}
              </span>
              <div className="flex items-center gap-1">
                {shortcut.keys.map((key, keyIndex) => (
                  <React.Fragment key={keyIndex}>
                    <kbd className="px-2 py-1 text-xs font-mono bg-muted rounded border border-border">
                      {key}
                    </kbd>
                    {keyIndex < shortcut.keys.length - 1 && (
                      <span className="text-muted-foreground text-xs">+</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="text-xs text-muted-foreground pt-2 border-t">
          Note: On Mac, use Cmd instead of Ctrl
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function KeyboardShortcutsButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      onClick={onClick}
      variant="ghost"
      size="sm"
      className="gap-2 text-muted-foreground hover:text-foreground"
      title="Keyboard shortcuts (?)"
    >
      <Keyboard size={16} />
      <span className="hidden sm:inline">Shortcuts</span>
    </Button>
  );
}
